import { useCallback, useEffect, useMemo, useRef } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import {
  FormBase,
  FormModal,
  type IFormModalProps,
  SelectData,
  TemplatesType,
  templatesTypes,
} from '@gbs-monorepo-packages/common';
import { zodResolver } from '@hookform/resolvers/zod';

import { VALIDATION_LIMITS } from '../../../../constants/ValidationRules';
import {
  type TemplateCreateSchema,
  templateCreateSchema,
} from '../../../TemplateCourses/templateSchema';

interface ISavePageAsTemplateModalProps extends Partial<
  Omit<IFormModalProps, 'children' | 'onAccept' | 'onOpenChange'>
> {
  onAccept: (title: string, templateOptions: number) => Promise<void>;
  onDecline: () => void;
  open: boolean;
  waitToOpen?: boolean;
  isUserAdmin?: boolean;
  isUserManager?: boolean;
}

const templatesOptionsFiltered = templatesTypes.filter(
  (i) => i.key !== TemplatesType.BUILD_SCRATCH
);

const templateOptionsClient = templatesTypes.filter(
  (i) => i.key === TemplatesType.MY_TEMPLATES
);

const templateOptionsManager = templatesTypes.filter(
  (i) =>
    i.key === TemplatesType.MY_TEMPLATES ||
    i.key === TemplatesType.SHARE_TEMPLATES
);

export const SavePageAsTemplateModal = ({
  onAccept,
  onDecline,
  open,
  waitToOpen = false,
  isUserAdmin = true,
  isUserManager = true,
  ...props
}: ISavePageAsTemplateModalProps): JSX.Element | null => {
  const isSelectOpen = useRef(new Set());

  const templateSchema = useForm<TemplateCreateSchema>({
    resolver: zodResolver(templateCreateSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      title: '',
      templateOptions: String(
        isUserAdmin
          ? TemplatesType.GLOBAL_TEMPLATES
          : TemplatesType.MY_TEMPLATES
      ),
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = templateSchema;

  const values = watch();

  const defaultTemplateOption = useMemo(
    () =>
      String(
        isUserAdmin
          ? TemplatesType.GLOBAL_TEMPLATES
          : TemplatesType.MY_TEMPLATES
      ),
    [isUserAdmin]
  );

  useEffect(() => {
    if (open) {
      setValue('templateOptions', defaultTemplateOption);
    }
  }, [open, setValue, defaultTemplateOption]);

  const handleDeclineSavePageTemplate = useCallback(() => {
    if (!isSelectOpen.current.size) {
      setValue('templateOptions', defaultTemplateOption);
      onDecline?.();
    }
  }, [onDecline, setValue, defaultTemplateOption]);

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectOpen.current.add(key);
    } else {
      isSelectOpen.current.delete(key);
    }
  }, []);

  const handleModalOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        handleDeclineSavePageTemplate();
      }
    },
    [handleDeclineSavePageTemplate]
  );

  const onSubmit: SubmitHandler<TemplateCreateSchema> = async (
    data: TemplateCreateSchema
  ) => {
    await onAccept?.(data.title, Number(data.templateOptions));
    reset();
    setValue('templateOptions', defaultTemplateOption);
  };

  return !open ? null : (
    <FormBase.Provider {...templateSchema}>
      <FormModal
        acceptText="Create Template"
        dataCy="title-newTemplate"
        declineText="Cancel"
        mainText="New Page Template"
        {...props}
        onAccept={handleSubmit(onSubmit)}
        onDecline={handleDeclineSavePageTemplate}
        onOpenChange={handleModalOpenChange}
        open={!waitToOpen}
      >
        <FormBase.Content data-cy="formBase-templateName">
          <FormBase.InputContent
            filled={!!values?.title}
            inputRef="template-name"
            label="Template Name"
            errorMessage={errors.title?.message}
            dataCy="label-templateName"
          >
            <FormBase.InputText
              dataCy="input-templateName"
              id="template-name"
              name="title"
              type="text"
              maxLength={VALIDATION_LIMITS.TEMPLATE.NAME_MAX}
              autoComplete="off"
            />
          </FormBase.InputContent>

          <FormBase.InputContent
            filled
            value={values.templateOptions}
            inputRef="select-template-options"
            label="Template Options"
            errorMessage={errors.templateOptions?.message}
            dataCy="label-templateType"
            isSelectData
          >
            <SelectData
              data={
                isUserAdmin
                  ? templatesOptionsFiltered
                  : isUserManager
                    ? templateOptionsManager
                    : templateOptionsClient
              }
              zIndex={10}
              value={values.templateOptions}
              dataCy="select-templateType"
              onValueChange={(id) => {
                setValue('templateOptions', id);
              }}
              onOpenChange={(isOpen) => {
                handleOpenChange(isOpen, 'options');
              }}
            />
          </FormBase.InputContent>
        </FormBase.Content>
      </FormModal>
    </FormBase.Provider>
  );
};
